import React from "react";
import PropTypes from "prop-types";
import injectSheet from "react-jss";
import classNames from "classnames";
import { IconArrowDown } from "folksam-ui";
import { SESSION_CAM_EXCLUDE } from "constants/sessioncam";
import themeOverride from "./themeOverride";
import { ThemeProvider } from "react-jss";
const styles = ({ theme }) => {
  return {
    root: {
      position: "relative",
      display: "inline-block",
      width: ({ full }) => (full ? "100%" : null)
    },
    wrapper: {
      boxSizing: "border-box",
      fontFamily: theme.fonts.serif,
      cursor: "pointer",
      color: "#512b2b",
      fontSize: "16px",
      height: "42px",
      maxWidth: "100%",
      width: "100%",
      appearance: "none"
    },
    container: {
      composes: "$wrapper",
      display: "flex",
      alignContent: "center",
      backgroundColor: "white",
      padding: ({ square }) => (square ? "0 30px 0 15px" : "0 45px 0 25px"),
      borderBottom: "2px solid",
      borderBottomColor: "#D3C3BC;",
      position: "absolute",
      pointerEvents: "none",
      color: "transparent",
      left: 0,
      top: 0
    },
    select: {
      composes: "$wrapper",
      border: 0,
      outline: 0,
      padding: ({ square }) => (square ? "0 30px 0 15px" : "0 45px 0 10px"),
      "&:focus + $container": {
        outline: 0,
        borderBottom: "2px solid",
        borderBottomColor: "#31a3ae"
      },
      "&:focus": {
        outline: 0,
        boxShadow: 0
      },
      "&::-ms-expand": {
        display: "none"
      }
    },
    label: {
      alignSelf: "center",
      whiteSpace: "nowrap",
      color: "#512b2b"
    },
    icon: {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      right: ({ square }) => (square ? 10 : 25),
      cursor: "pointer",
      pointerEvents: "none",
      fill: ({ dark, disabled }) =>
        dark
          ? theme.colors.white
          : disabled
          ? theme.colors.primary3
          : theme.colors.link
    }
  };
};
export const FormInputSelectOption = ({
  children,
  value,
  label,
  disabled,
  hidden
}) => (
  <option disabled={disabled} value={value} hidden={hidden}>
    {children}
  </option>
);

const FormInputSelect = React.forwardRef(
  (
    {
      classes,
      children,
      square,
      dark,
      full,
      value: selected,
      onChange,
      invalid,
      theme,
      sessionCamExclude,
      ...props
    },
    ref
  ) => {
    const rootClasses = classNames(classes.root, {
      [classes.dark]: dark,
      [classes.invalid]: invalid,
      [SESSION_CAM_EXCLUDE]: sessionCamExclude
    });
    /**
     * collect the label from each of the options
     * this will be used as the display value instead of the actual text in the option if present
     */
    const labelsMap = React.Children.toArray(children).reduce(
      (acc, { props: { label, value, children } }) => {
        acc[value] = label ? label : children;
        return acc;
      },
      {}
    );

    return (
      <ThemeProvider theme={{ theme: themeOverride }}>
        <div className={rootClasses}>
          <select
            className={classes.select}
            ref={ref}
            value={selected}
            onChange={onChange}
            {...props}
          >
            {children}
          </select>
          <div className={classes.container}>
            <span className={classes.label}>{labelsMap[selected]}</span>
            <IconArrowDown
              className={classes.icon}
              width={"10px"}
              height={"8px"}
            />
          </div>
        </div>
      </ThemeProvider>
    );
  }
);
FormInputSelect.displayName = "FormInputSelect";
FormInputSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  id: PropTypes.string.isRequired,
  /** make the input more square in appearance */
  square: PropTypes.bool,
  dark: PropTypes.bool,
  /** will make the select take 100% width */
  full: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  /** Indicates if this field is invalid */
  invalid: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  ref: PropTypes.func,
  /** Indicates if this field has been excluded from recording by sessioncam */
  sessionCamExclude: PropTypes.bool
};
FormInputSelectOption.displayName = "FormInputSelect.Option";
FormInputSelectOption.propTypes = {
  value: PropTypes.string.isRequired,
  /** custom display value to display in the box instead of the children text */
  label: PropTypes.string,
  /** disable an option */
  disabled: PropTypes.bool,
  children: PropTypes.string.isRequired,
  hidden: PropTypes.bool
};
FormInputSelect.Option = FormInputSelectOption;

export default injectSheet(styles, { inject: ["classes"] })(FormInputSelect);
